import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Check, Sparkles, Info, Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { useBalance } from "@/lib/balance-context";
import { billing } from "@/lib/api";
import PageHead from "@/components/PageHead";
import BetaNotice from "@/components/BetaNotice";

const FEATURES = {
  free: [
    "3 lifetime message credits",
    "AI Compose for notes & titles",
    "Email + SMS delivery on release",
    "Recipient verification & secure playback",
  ],
  paygo: [
    "One message credit per purchase",
    "Credits never expire",
    "All delivery features included",
    "No commitment, no recurring charges",
  ],
  membership: [
    "£1 per additional message (instead of £2.99)",
    "No monthly reset — no included messages",
    "Priority support & new features first",
    "Cancel anytime",
  ],
};

export default function Pricing() {
  const { user } = useAuth();
  const { balance } = useBalance();
  const isMember = balance?.plan === "subscriber";
  const [busy, setBusy] = useState(null); // "membership" | "message" | "portal" | null

  const startCheckout = async (kind) => {
    if (!user) {
      toast.info("Sign in first to unlock checkout");
      window.location.href = "/login";
      return;
    }
    setBusy(kind);
    try {
      const fn = kind === "membership" ? billing.checkoutMembership : billing.checkoutMessage;
      const { checkout_url } = await fn();
      window.location.href = checkout_url;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not start checkout");
      setBusy(null);
    }
  };

  const openPortal = async () => {
    setBusy("portal");
    try {
      const { portal_url } = await billing.portal();
      window.location.href = portal_url;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not open customer portal");
      setBusy(null);
    }
  };

  const messagePrice = isMember ? "£1" : "£2.99";
  const messageTagline = isMember
    ? "Member rate is active. Each top-up adds one credit."
    : "For occasional senders. Buy exactly the messages you need.";

  return (
    <>
      <PageHead
        title="ChronoVaults Pricing | Future Messages from £2.99"
        description="Three lifetime messages free. Pay-as-you-go from £2.99 per message, or become a member for £5/month and unlock £1 per message."
        canonical="https://chronovaults.co.uk/pricing"
      />
    <div className="max-w-6xl mx-auto px-6 md:px-10 py-16">
      <p className="label-mono text-terracotta">Pricing</p>
      <h1 className="font-serif text-5xl text-bone mt-3 leading-none">Simple, honest pricing.</h1>
      <p className="text-white/60 mt-4 max-w-xl text-sm">
        Three messages free forever. After that, pay per message — or become a member for a lower per-message rate. These are our early-user Beta rates.
      </p>

      <BetaNotice variant="detailed" />

      <div className="mt-12 grid md:grid-cols-3 gap-px bg-white/5">
        {/* FREE */}
        <div className="bg-vault-950 p-8 flex flex-col" data-testid="pricing-tier-free">
          <p className="label-mono">Free</p>
          <div className="mt-6">
            <span className="font-serif text-5xl text-bone">£0</span>
            <span className="text-xs text-white/50 ml-2">3 lifetime messages</span>
          </div>
          <p className="text-white/60 mt-4 text-sm min-h-[3rem]">
            Try ChronoVaults with three sealed messages — no card required.
          </p>
          <ul className="mt-6 space-y-3 flex-1">
            {FEATURES.free.map((f) => (
              <li key={f} className="flex items-start gap-2 text-sm text-white/70">
                <Check className="w-4 h-4 mt-0.5 text-terracotta flex-shrink-0" strokeWidth={1.5} />
                <span>{f}</span>
              </li>
            ))}
          </ul>
          <Button
            disabled
            data-testid="pricing-cta-free"
            className="mt-8 w-full h-11 bg-terracotta hover:bg-terracotta/90 text-white rounded-sm disabled:bg-vault-700 disabled:text-white/40 disabled:cursor-not-allowed"
          >
            Included with your account
          </Button>
        </div>

        {/* PAYGO / TOP-UP */}
        <div className="bg-vault-950 p-8 flex flex-col" data-testid="pricing-tier-paygo">
          <p className="label-mono">Pay as you go</p>
          <div className="mt-6">
            <span className="font-serif text-5xl text-bone">{messagePrice}</span>
            <span className="text-xs text-white/50 ml-2">per additional message</span>
          </div>
          <p className="text-white/60 mt-4 text-sm min-h-[3rem]">{messageTagline}</p>
          <ul className="mt-6 space-y-3 flex-1">
            {FEATURES.paygo.map((f) => (
              <li key={f} className="flex items-start gap-2 text-sm text-white/70">
                <Check className="w-4 h-4 mt-0.5 text-terracotta flex-shrink-0" strokeWidth={1.5} />
                <span>{f}</span>
              </li>
            ))}
          </ul>
          <Button
            onClick={() => startCheckout("message")}
            disabled={busy !== null}
            data-testid="pricing-cta-paygo"
            className="mt-8 w-full h-11 bg-terracotta hover:bg-terracotta/90 text-white rounded-sm disabled:bg-vault-700 disabled:text-white/40 disabled:cursor-not-allowed"
          >
            {busy === "message" ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" /> Redirecting…
              </>
            ) : (
              `Buy 1 message · ${messagePrice}`
            )}
          </Button>
        </div>

        {/* MEMBERSHIP */}
        <div className="bg-vault-950 p-8 flex flex-col relative" data-testid="pricing-tier-membership">
          <span className="absolute top-4 right-4 label-mono text-terracotta flex items-center gap-1">
            <Sparkles className="w-3 h-3" /> Best per-message rate
          </span>
          <p className="label-mono">Membership</p>
          <div className="mt-6">
            <span className="font-serif text-5xl text-bone">£5</span>
            <span className="text-xs text-white/50 ml-2">per month</span>
          </div>
          <p className="text-white/60 mt-4 text-sm min-h-[3rem]">
            For regular senders — unlocks the £1 per-message price.
          </p>
          <ul className="mt-6 space-y-3 flex-1">
            {FEATURES.membership.map((f) => (
              <li key={f} className="flex items-start gap-2 text-sm text-white/70">
                <Check className="w-4 h-4 mt-0.5 text-terracotta flex-shrink-0" strokeWidth={1.5} />
                <span>{f}</span>
              </li>
            ))}
          </ul>
          {isMember ? (
            <Button
              onClick={openPortal}
              disabled={busy !== null}
              data-testid="pricing-cta-portal"
              variant="outline"
              className="mt-8 w-full h-11 border-white/20 bg-transparent text-bone hover:bg-white/5 rounded-sm"
            >
              {busy === "portal" ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" /> Opening portal…
                </>
              ) : (
                "Manage subscription"
              )}
            </Button>
          ) : (
            <Button
              onClick={() => startCheckout("membership")}
              disabled={busy !== null}
              data-testid="pricing-cta-membership"
              className="mt-8 w-full h-11 bg-terracotta hover:bg-terracotta/90 text-white rounded-sm disabled:bg-vault-700 disabled:text-white/40 disabled:cursor-not-allowed"
            >
              {busy === "membership" ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" /> Redirecting…
                </>
              ) : (
                "Become a member · £5/mo"
              )}
            </Button>
          )}
        </div>
      </div>

      <div className="mt-10 flex items-start gap-3 p-5 border border-white/10 bg-vault-800">
        <Info className="w-4 h-4 text-terracotta mt-0.5 flex-shrink-0" strokeWidth={1.5} />
        <p className="text-sm text-white/60 leading-relaxed">
          Membership never includes free messages — it lowers the price of the ones you buy. Payments are handled by
          Stripe in test mode; use card <code className="text-bone">4242 4242 4242 4242</code>, any future expiry, any
          CVC. Questions?{" "}
          <a href="mailto:info@chronovaults.co.uk" className="text-terracotta hover:underline">
            info@chronovaults.co.uk
          </a>
          .
        </p>
      </div>

      <p className="mt-10 text-center">
        <Link to="/dashboard" className="text-xs text-white/50 hover:text-terracotta label-mono" data-testid="pricing-back">
          ← Back to your vault
        </Link>
      </p>
    </div>
    </>
  );
}
